/**
 * Life4Earth V2 — Finance Series Points API Adapter
 *
 * Mirrors: api/scheduleSeriesPoints.ts but for finance cost data.
 * Endpoint: /api/FinanceSeriesPoints
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types ────────────────────────────────────────────────────

export interface BudgetSeriesPointResponse {
  id: string;
  projectId: string;
  instanceGuid: string | null;
  reportingDate: string; // "YYYY-MM-DD"
  baselineBudget: number | null;
  actualSpend: number | null;
  forecastSpend: number | null;
  createdAt: string;
}

export interface CreateBudgetSeriesPointRequest {
  projectId: string;
  instanceGuid?: string;
  reportingDate: string;
  baselineBudget?: number;
  actualSpend?: number;
  forecastSpend?: number;
}

// ── API Functions ────────────────────────────────────────────

/**
 * Fetch ALL finance series points across all projects.
 * Used by App.tsx to build the finance data lookup.
 */
export async function fetchAllBudgetSeriesPoints(): Promise<
  BudgetSeriesPointResponse[]
> {
  return apiFetch<BudgetSeriesPointResponse[]>(
    "/api/FinanceSeriesPoints/all"
  );
}

/**
 * Convert API response rows into the seed-data shape that App.tsx
 * and the kpi-engine expect (project_GUID, reporting_date, etc.).
 *
 * DEV-851: `created_at` preserved for the data-reliability engine's
 * `progress_entries` input. See schedulePointToSeedShape for the same pattern.
 */
export function financePointToSeedShape(
  p: BudgetSeriesPointResponse
): Record<string, any> {
  return {
    project_GUID: normalizeGuid(p.projectId),
    instance_guid: normalizeGuid(p.instanceGuid),
    reporting_date: p.reportingDate,
    baseline_budget: p.baselineBudget ?? null,
    actual_spend: p.actualSpend ?? null,
    forecast_spend: p.forecastSpend ?? null,
    created_at: p.createdAt,
  };
}

/**
 * Fetch finance series points for a project+instance.
 * Results ordered by reporting_date DESC (most recent first).
 */
export async function fetchBudgetSeriesPoints(
  projectId: string,
  instanceGuid?: string,
  limit?: number
): Promise<BudgetSeriesPointResponse[]> {
  const params = new URLSearchParams({ projectId });
  if (instanceGuid) params.set("instanceGuid", instanceGuid);
  if (limit) params.set("limit", limit.toString());

  return apiFetch<BudgetSeriesPointResponse[]>(
    `/api/FinanceSeriesPoints?${params.toString()}`
  );
}

export async function createBudgetSeriesPoint(
  request: CreateBudgetSeriesPointRequest
): Promise<BudgetSeriesPointResponse> {
  return apiFetch<BudgetSeriesPointResponse>("/api/FinanceSeriesPoints", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Batch upsert: insert or update multiple points at once.
 * Matched on (project_id, instance_guid, reporting_date).
 * Max 500 points per batch.
 */
export async function batchUpsertBudgetSeriesPoints(
  points: CreateBudgetSeriesPointRequest[]
): Promise<BudgetSeriesPointResponse[]> {
  return apiFetch<BudgetSeriesPointResponse[]>(
    "/api/FinanceSeriesPoints/batch",
    {
      method: "POST",
      body: JSON.stringify({ points }),
    }
  );
}

export async function deleteBudgetSeriesPoint(
  id: string
): Promise<void> {
  return apiFetch<void>(`/api/FinanceSeriesPoints/${id}`, {
    method: "DELETE",
  });
}

/**
 * Delete finance series points by date range.
 * Removes all points for the given project+instance that fall outside
 * the specified start/end window.
 * Used by the Apply handler to clean up stale records after a date-range change.
 */
export async function deleteBudgetSeriesPointsByDateRange(
  projectId: string,
  instanceGuid: string | undefined,
  keepStartDate: string,
  keepEndDate: string,
  options?: { softDelete?: boolean },
): Promise<{ deletedCount: number }> {
  return apiFetch<{ deletedCount: number }>(
    "/api/FinanceSeriesPoints/cleanup-range",
    {
      method: "POST",
      body: JSON.stringify({
        projectId,
        instanceGuid: instanceGuid || null,
        keepStartDate,
        keepEndDate,
        softDelete: !!options?.softDelete,
      }),
    }
  );
}
